// Additional detail for each practice area — "Who this serves" and
// "Typical matters" callouts. Kept separate from practiceAreas.js so the
// primary data file stays lean and can be maintained by non-devs later.
//
// Missing entries are treated as empty (the callouts simply don't render).

export const PRACTICE_AREA_DETAILS = {
  "business-transactions": {
    serves: [
      "Founders and early-stage operating teams",
      "Closely-held companies without in-house counsel",
      "Companies serving as outside general counsel on ongoing corporate matters",
      "Investors, syndicates, and family offices",
    ],
    typicalMatters: [
      "Entity formation and operating-agreement negotiation",
      "Priced-round and SAFE financings",
      "Vendor, customer, and licensing agreements",
      "Founder equity and vesting arrangements",
      "Ownership changes, buyouts, and exits",
    ],
  },
  "international-trade": {
    serves: [
      "U.S. importers and exporters of goods, services, and technology",
      "Foreign companies selling into the U.S. market",
      "Companies operating in sanctioned or high-risk jurisdictions",
      "Distributors and cross-border joint venture parties",
    ],
    typicalMatters: [
      "OFAC screening programs and sanctions compliance",
      "Voluntary self-disclosures and enforcement responses",
      "Customs classification, valuation, and prior disclosures",
      "Cross-border commercial agreements and structuring",
    ],
  },
  "export-compliance": {
    serves: [
      "Semiconductor, aerospace, and defense-adjacent companies",
      "Software and encryption technology developers",
      "Universities and research institutions",
      "Companies scaling exports, licensing, or foreign hires",
    ],
    typicalMatters: [
      "ITAR and EAR jurisdiction and classification analyses",
      "Export authorization applications and license determinations",
      "Deemed export and foreign national access reviews",
      "Voluntary self-disclosures and enforcement matters",
      "Compliance program design and training",
    ],
  },
  "cfius": {
    serves: [
      "U.S. businesses accepting foreign investment",
      "Foreign investors evaluating U.S. targets",
      "Sellers in cross-border M&A",
      "Companies operating in critical technology, infrastructure, or data",
    ],
    typicalMatters: [
      "CFIUS jurisdiction analyses (covered transactions and covered investments)",
      "Mandatory and voluntary filings (declarations and full notices)",
      "TID U.S. business assessments (critical tech, infrastructure, data)",
      "Mitigation agreement negotiation",
      "Post-close monitoring and non-notified transaction responses",
    ],
  },
  "foci": {
    serves: [
      "U.S. cleared contractors under FOCI",
      "Foreign investors in cleared or clearance-eligible U.S. businesses",
      "Defense-adjacent companies with foreign ownership on the cap table",
      "Companies preparing for or holding a facility clearance (FCL)",
    ],
    typicalMatters: [
      "SF-328 preparation and updates",
      "FOCI mitigation instrument selection (Board Resolution, SCA, SSA, VT, Proxy)",
      "Facility clearance sponsorship and KMP eligibility",
      "Technology Control Plans and Electronic Communications Plans",
      "Annual FOCI certifications, changed-condition reporting, and DCSA responses",
    ],
  },
  "government-contracts": {
    serves: [
      "Emerging companies pursuing federal contracts",
      "Small-business and 8(a) participants",
      "Prime and subcontractor teams",
      "Companies responding to Buy American, TAA, or FAR flow-down requirements",
    ],
    typicalMatters: [
      "Solicitation review, teaming agreements, and subcontracts",
      "FAR and DFARS compliance",
      "Cybersecurity flow-downs (CMMC, DFARS 7012, NIST 800-171)",
      "Bid protests and disputes",
      "Contract close-out and audit responses",
    ],
  },
  "intellectual-property": {
    serves: [
      "Founders and inventors protecting core technology",
      "Software and AI companies",
      "Companies licensing in or out of a technology portfolio",
      "Employers protecting confidential information and trade secrets",
    ],
    typicalMatters: [
      "IP assignment, invention, and confidentiality agreements",
      "Trademark strategy and clearance",
      "Trade secret programs and dispute posture",
      "In-bound and out-bound licensing",
      "IP diligence in financings and M&A",
    ],
  },
  "artificial-intelligence": {
    serves: [
      "AI developers, foundation-model users, and downstream deployers",
      "Companies integrating AI into regulated products",
      "Enterprise buyers evaluating AI vendor risk",
      "Startups whose training or deployment implicates export controls",
    ],
    typicalMatters: [
      "AI model licensing, terms of use, and content-provenance provisions",
      "AI-related export control questions (foundation models, compute, weights)",
      "Data-use and training-data agreements",
      "AI-specific vendor and enterprise contracting",
      "Emerging AI regulatory analysis (federal and state)",
    ],
  },
  "semiconductor-industry": {
    serves: [
      "Fabless, EDA, IP, and equipment companies",
      "Compound-semiconductor and advanced-packaging startups",
      "Companies pursuing CHIPS Act funding or partnerships",
      "Investors in the semiconductor value chain",
    ],
    typicalMatters: [
      "Export controls on advanced semiconductors, compute, and manufacturing equipment",
      "CFIUS analyses on semiconductor investments and JVs",
      "CHIPS Act application and compliance",
      "Technology licensing and cross-license negotiation",
      "Cross-border foundry and supplier agreements",
    ],
  },
  "fcc": {
    serves: [
      "Wireless, satellite, and RF technology companies",
      "Communications equipment manufacturers",
      "Companies deploying radios in regulated bands",
    ],
    typicalMatters: [
      "Equipment authorization (certification, SDoC, verification)",
      "Experimental and market-access authorizations",
      "Part 15, Part 22-27, and satellite service applications",
      "Enforcement responses and consent negotiation",
    ],
  },
  "insurance": {
    serves: [
      "Insureds negotiating coverage in complex commercial policies",
      "Companies pursuing coverage for regulatory investigations or enforcement",
      "Cross-border operations placing insurance across jurisdictions",
    ],
    typicalMatters: [
      "Policy review and coverage-gap analysis at purchase and renewal",
      "Claim submission and denial responses",
      "Coverage for regulatory investigations, D&O, and cyber matters",
      "Coordination with insurance brokers and underwriters",
    ],
  },
};

export function getPracticeAreaDetail(slug) {
  return PRACTICE_AREA_DETAILS[slug];
}
